'use client';

import * as React from 'react';

import { DialogDescription, DialogTitle } from '@/components/ui/layout/dialog';
import { cn } from '@/lib/utils';

export type DialogHeadingProps = {
  /** Small uppercase kicker rendered above the title. Ignored when `compact`. */
  eyebrow?: React.ReactNode;
  /** Main heading. Wrapped in Radix DialogTitle so screen readers announce it. */
  title: React.ReactNode;
  /** Optional subtitle. Wrapped in Radix DialogDescription. */
  description?: React.ReactNode;
  /** Icon or avatar slot rendered to the left of the text stack. */
  leading?: React.ReactNode;
  /** Action slot rendered at the far right (e.g. inline button or link). */
  trailing?: React.ReactNode;
  /**
   * Compact one-line variant: renders the title in `text-overline` style
   * (small uppercase) with tighter padding. Use when the dialog body is
   * dense and the heading should recede (e.g. filter-heavy dialogs).
   */
  compact?: boolean;
  /** Escape hatch for padding/border tweaks on the outer wrapper. */
  className?: string;
};

/**
 * Canonical dialog header. Composes eyebrow + title + description with
 * Radix DialogTitle/DialogDescription, standard padding (with right
 * padding reserved for the close button), and a bottom border.
 */
export function DialogHeading({
  eyebrow,
  title,
  description,
  leading,
  trailing,
  compact = false,
  className,
}: DialogHeadingProps) {
  return (
    <div
      className={cn(
        'border-b border-border text-left',
        compact
          ? 'px-4 pr-12 py-2.5 sm:px-6 sm:pr-14 sm:py-3'
          : 'px-4 pr-12 py-4 sm:px-6 sm:pr-14 sm:py-5',
        leading ? 'flex items-start gap-3' : null,
        !leading && trailing ? 'flex items-start justify-between gap-4' : null,
        className,
      )}
    >
      {leading ? <div className="shrink-0">{leading}</div> : null}
      <div className={cn('min-w-0', (leading || trailing) ? 'flex-1' : null)}>
        {eyebrow && !compact ? (
          <p className="text-overline text-muted-foreground">{eyebrow}</p>
        ) : null}
        <DialogTitle
          className={cn(
            'text-left',
            compact ? 'text-overline text-foreground' : 'text-title-lg text-foreground',
            !compact && eyebrow ? 'mt-2' : null,
          )}
        >
          {title}
        </DialogTitle>
        {description ? (
          <DialogDescription
            className={cn(
              'text-muted-foreground',
              compact ? 'mt-1 text-caption' : 'mt-2 text-body-lg',
            )}
          >
            {description}
          </DialogDescription>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </div>
  );
}
